import { useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  LinearProgress,
  Button,
  Divider,
  Chip,
  Tooltip,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  PowerOff,
  Power,
  Settings,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Building2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInstitutes } from '../../hooks/useInstitutes';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { TenantProvisioningDto, TenantOnboardingDto } from '../../types/institute';

const PROVISION_STEPS: { key: keyof TenantProvisioningDto; label: string }[] = [
  { key: 'databaseCreated', label: 'Database Created' },
  { key: 'defaultDataSeeded', label: 'Default Data Seeded' },
  { key: 'adminUserCreated', label: 'Admin User Created' },
  { key: 'storageConfigured', label: 'Storage Configured' },
  { key: 'emailConfigured', label: 'Email Configured' },
  { key: 'smsConfigured', label: 'SMS Configured' },
  { key: 'paymentGatewayConfigured', label: 'Payment Gateway' },
  { key: 'brandingConfigured', label: 'Branding Configured' },
];

const ONBOARD_STEPS: { key: keyof TenantOnboardingDto; label: string }[] = [
  { key: 'instituteProfileCompleted', label: 'Institute Profile' },
  { key: 'logoUploaded', label: 'Logo Uploaded' },
  { key: 'academicYearCreated', label: 'Academic Year' },
  { key: 'classesCreated', label: 'Classes Created' },
  { key: 'studentsImported', label: 'Students Imported' },
  { key: 'feeStructureCreated', label: 'Fee Structure' },
  { key: 'adminTrainingCompleted', label: 'Admin Training' },
  { key: 'readyForGoLive', label: 'Ready for Go-Live' },
];

const FEATURE_FLAGS: { key: string; label: string; destructive?: boolean }[] = [
  { key: 'fees', label: 'Fees Module', destructive: true },
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', fontSize: '0.65rem', letterSpacing: '0.08em', display: 'block', mb: 1 }}
    >
      {children}
    </Typography>
  );
}

function ChecklistItem({ done, label }: { done: boolean; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      {done
        ? <CheckCircle2 size={13} color="#10B981" />
        : <Circle size={13} color="#334155" />}
      <Typography variant="body2" sx={{ color: done ? 'text.primary' : 'text.disabled', fontSize: '0.78rem' }}>
        {label}
      </Typography>
    </Box>
  );
}

function Card({ children, sx = {} }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

interface Props {
  id: number;
  onClose: () => void;
}

export function InstituteDetailDrawer({ id, onClose }: Props) {
  const navigate = useNavigate();
  const { useDetail, useActivate, useFeatureFlags, useSetFeatureFlag } = useInstitutes();
  const { data: institute, isLoading } = useDetail(id);
  const activateMutation = useActivate();
  const { data: featureFlags = {} } = useFeatureFlags(id);
  const setFeatureFlagMutation = useSetFeatureFlag(id);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingActivate, setPendingActivate] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingFlagKey, setPendingFlagKey] = useState<string | null>(null);
  const [pendingFlagValue, setPendingFlagValue] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[...Array(7)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={36} sx={{ borderRadius: '7px' }} />
        ))}
      </Box>
    );
  }

  if (!institute) return null;

  const handleDeactivateClick = () => {
    setDeactivationReason('');
    setReasonError('');
    setPendingActivate(false);
    setConfirmOpen(true);
  };

  const handleActivateClick = () => {
    setPendingActivate(true);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingActivate && !deactivationReason.trim()) {
      setReasonError('Please provide a reason for deactivation');
      return;
    }
    activateMutation.mutate({
      id,
      activate: pendingActivate,
      reason: pendingActivate ? undefined : deactivationReason.trim(),
    });
    setConfirmOpen(false);
    setDeactivationReason('');
  };

  const handleFlagToggle = (key: string, newValue: boolean, destructive?: boolean) => {
    if (destructive && !newValue) {
      setPendingFlagKey(key);
      setPendingFlagValue(newValue);
      setConfirmOpen(false);
      // use a separate confirm for destructive flags
      setPendingActivate(false);
    } else {
      setFeatureFlagMutation.mutate({ featureKey: key, isEnabled: newValue });
    }
  };

  const handleFlagConfirm = () => {
    if (pendingFlagKey) {
      setFeatureFlagMutation.mutate({ featureKey: pendingFlagKey, isEnabled: pendingFlagValue });
      setPendingFlagKey(null);
    }
  };

  return (
    <Box sx={{ p: 2.5, overflowY: 'auto', height: 'calc(100vh - 60px)' }}>

      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 36, height: 36, borderRadius: '8px',
                backgroundColor: 'rgba(138,112,214,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Building2 size={16} color="#8A70D6" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {institute.instituteName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                {institute.code}
              </Typography>
            </Box>
          </Box>
          <StatusBadge status={institute.isActive ? 'Active' : 'Inactive'} />
        </Box>
        {institute.email && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
            {institute.email}
          </Typography>
        )}
      </Box>

      {/* Status banner for inactive institutes */}
      {!institute.isActive && (
        <Alert
          severity="warning"
          icon={<AlertTriangle size={15} />}
          sx={{
            mb: 2, py: 0.75, fontSize: '0.78rem',
            backgroundColor: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.2)',
            color: '#F59E0B',
            '& .MuiAlert-icon': { color: '#F59E0B', pt: '6px' },
          }}
        >
          This institute is deactivated. Users cannot log in.
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {institute.isActive ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<PowerOff size={13} />}
            onClick={handleDeactivateClick}
            disabled={activateMutation.isPending}
            sx={{
              fontSize: '0.75rem',
              backgroundColor: 'rgba(239,68,68,0.12)',
              color: '#EF4444',
              border: '1px solid rgba(239,68,68,0.3)',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'rgba(239,68,68,0.2)', boxShadow: 'none' },
            }}
          >
            Deactivate
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={<Power size={13} />}
            onClick={handleActivateClick}
            disabled={activateMutation.isPending}
            sx={{
              fontSize: '0.75rem',
              backgroundColor: 'rgba(16,185,129,0.12)',
              color: '#10B981',
              border: '1px solid rgba(16,185,129,0.3)',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'rgba(16,185,129,0.2)', boxShadow: 'none' },
            }}
          >
            Activate
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<ExternalLink size={13} />}
          onClick={() => { navigate(`/audit-logs?instituteId=${id}`); onClose(); }}
          sx={{ fontSize: '0.75rem' }}
        >
          Audit Logs
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Settings size={13} />}
          endIcon={settingsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          onClick={() => setSettingsOpen((v) => !v)}
          sx={{ fontSize: '0.75rem', ml: 'auto' }}
        >
          Features
        </Button>
      </Box>

      {/* Feature Flags Panel */}
      <Collapse in={settingsOpen}>
        <Card sx={{ mb: 2.5 }}>
          <SectionLabel>Feature Flags</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {FEATURE_FLAGS.map(({ key, label, destructive }) => {
              const val = featureFlags[key] ?? true;
              return (
                <FormControlLabel
                  key={key}
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      {label}
                    </Typography>
                  }
                  control={
                    <Switch
                      checked={val}
                      size="small"
                      disabled={setFeatureFlagMutation.isPending}
                      onChange={(e) => handleFlagToggle(key, e.target.checked, destructive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#8A70D6' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#8A70D6',
                        },
                      }}
                    />
                  }
                  sx={{ mx: 0, my: 0.25 }}
                />
              );
            })}
          </Box>
        </Card>
      </Collapse>

      <Divider sx={{ mb: 2.5 }} />

      {/* Onboarding Progress */}
      {institute.onboarding && (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <SectionLabel>Onboarding Progress</SectionLabel>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.8rem' }}>
              {institute.onboarding.overallProgressPercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={institute.onboarding.overallProgressPercent}
            sx={{
              mb: 1.25, height: 5, borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #8A70D6, #10B981)',
                borderRadius: 3,
              },
            }}
          />
          <Card>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {ONBOARD_STEPS.map(({ key, label }) => (
                <ChecklistItem
                  key={key}
                  done={!!institute.onboarding![key as keyof TenantOnboardingDto]}
                  label={label}
                />
              ))}
            </Box>
            {institute.onboarding.assignedOnboardingManager && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  Manager: {institute.onboarding.assignedOnboardingManager}
                </Typography>
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* Provisioning */}
      {institute.provisioning && (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <SectionLabel>Provisioning</SectionLabel>
            <Chip
              label={institute.provisioning.status}
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(138,112,214,0.15)', color: '#8A70D6' }}
            />
          </Box>
          <Card>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              {PROVISION_STEPS.map(({ key, label }) => (
                <ChecklistItem
                  key={key}
                  done={!!institute.provisioning![key as keyof TenantProvisioningDto]}
                  label={label}
                />
              ))}
            </Box>
            {institute.provisioning.lastError && (
              <Box
                sx={{
                  mt: 1, p: 1, borderRadius: '6px',
                  backgroundColor: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#EF4444', fontSize: '0.7rem' }}>
                  Error: {institute.provisioning.lastError}
                </Typography>
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* Activate confirm */}
      <ConfirmDialog
        open={confirmOpen && pendingActivate}
        title="Activate Institute"
        message={`Are you sure you want to activate "${institute.instituteName}"? Users will regain access immediately.`}
        confirmLabel="Activate"
        severity="warning"
        loading={activateMutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Deactivate dialog — inline below action bar, not using ConfirmDialog (needs text input) */}
      {confirmOpen && !pendingActivate && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setConfirmOpen(false); } }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 420,
              borderRadius: '12px',
              backgroundColor: '#1A1B23',
              border: '1px solid rgba(255,255,255,0.08)',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: '8px',
                  backgroundColor: 'rgba(239,68,68,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <PowerOff size={16} color="#EF4444" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Deactivate Institute
              </Typography>
            </Box>

            <Alert
              severity="error"
              icon={<AlertTriangle size={15} />}
              sx={{
                mb: 2, py: 0.75, fontSize: '0.78rem',
                backgroundColor: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                color: '#EF4444',
                '& .MuiAlert-icon': { color: '#EF4444', pt: '6px' },
              }}
            >
              <strong>{institute.instituteName}</strong> will be immediately deactivated. All users will lose access.
            </Alert>

            <TextField
              label="Reason for deactivation"
              placeholder="e.g. Subscription expired, policy violation…"
              value={deactivationReason}
              onChange={(e) => { setDeactivationReason(e.target.value); setReasonError(''); }}
              error={!!reasonError}
              helperText={reasonError || 'Required — visible to audit logs'}
              multiline
              rows={3}
              fullWidth
              size="small"
              sx={{ mb: 2.5 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setConfirmOpen(false); setDeactivationReason(''); setReasonError(''); }}
                sx={{ fontSize: '0.8rem' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PowerOff size={13} />}
                onClick={handleConfirm}
                disabled={activateMutation.isPending}
                sx={{
                  fontSize: '0.8rem',
                  backgroundColor: '#EF4444',
                  '&:hover': { backgroundColor: '#DC2626' },
                }}
              >
                {activateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Destructive flag confirm */}
      <ConfirmDialog
        open={pendingFlagKey !== null}
        title="Disable Feature"
        message={`Disabling this feature will immediately affect active users. Are you sure?`}
        confirmLabel="Disable"
        severity="danger"
        loading={setFeatureFlagMutation.isPending}
        onConfirm={handleFlagConfirm}
        onCancel={() => setPendingFlagKey(null)}
      />
    </Box>
  );
}
